/**
 * Monoova's Hosted UI card checkout widget.
 *
 * Monoova's own docs (api-docs.monoova.com/cards) don't publicly document the frontend
 * SDK contract. Monoova's public "merchantSDK" GitHub sample shows their Hosted UI is
 * Primer.io's Universal Checkout under the hood - loaded from sdk.primer.io and driven
 * by Primer.showUniversalCheckout(clientToken, {...}) - and the BTB migration doc's field
 * names (clientToken, traceId) match Primer's vocabulary.
 */

const PRIMER_VERSION = '2.62.1';
const PRIMER_SCRIPT_URL = `https://sdk.primer.io/web/v${PRIMER_VERSION}/Primer.min.js`;
const PRIMER_STYLESHEET_URL = `https://sdk.primer.io/web/v${PRIMER_VERSION}/Checkout.css`;

export type PrimerCheckoutPayment = Record<string, unknown>;

type UniversalCheckoutHandle = {
  submit: () => void,
};

type PrimerGlobal = {
  showUniversalCheckout: (clientToken: string, options: Record<string, unknown>) => Promise<UniversalCheckoutHandle>,
};

declare global {
  interface Window {
    Primer?: PrimerGlobal,
  }
}

export type PrimerCheckoutFailHandler = {
  showErrorMessage: (message?: string) => void,
};

export type MountCardCheckoutCallbacks = {
  onComplete: (payment: PrimerCheckoutPayment) => void,
  onFail: (error: unknown, payment: PrimerCheckoutPayment | undefined, handler: PrimerCheckoutFailHandler | undefined) => void,
  onCancel?: () => void,
  /**
   * Fires whenever Primer's own readiness check for its (hidden) built-in submit
   * button changes - i.e. whether the entered card details are currently valid
   * enough to submit. Used to guard our own "Confirm Order" button from calling
   * submit() while the card form is incomplete.
   */
  onSubmittableChange: (submittable: boolean) => void,
};

/**
 * Handle returned once the card checkout widget is mounted, used to trigger
 * submission from our own "Confirm Order" button instead of Primer's built-in one.
 */
export type CardCheckoutHandle = {
  submit: () => void,
};

let loadPromise: Promise<PrimerGlobal> | null = null;

function loadPrimerSdk (): Promise<PrimerGlobal> {
  if (window.Primer) {
    return Promise.resolve(window.Primer);
  }

  if (!loadPromise) {
    loadPromise = new Promise<PrimerGlobal>((resolve, reject) => {
      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = PRIMER_STYLESHEET_URL;
      document.head.appendChild(stylesheet);

      const script = document.createElement('script');
      script.src = PRIMER_SCRIPT_URL;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        if (window.Primer) {
          resolve(window.Primer);
        } else {
          reject(new Error('Primer SDK script loaded but window.Primer is unavailable'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load Primer SDK script'));
      document.head.appendChild(script);
    }).catch((error) => {
      loadPromise = null;
      throw error;
    });
  }

  return loadPromise;
}

let containerIdCounter = 0;

export async function mountCardCheckout (
  clientToken: string,
  container: HTMLElement,
  callbacks: MountCardCheckoutCallbacks
): Promise<CardCheckoutHandle> {
  const primer = await loadPrimerSdk();

  if (!container.id) {
    containerIdCounter += 1;
    container.id = `monoova-card-checkout-${containerIdCounter}`;
  }

  const checkout = await primer.showUniversalCheckout(clientToken, {
    container: `#${container.id}`,
    apiVersion: '2.4',
    // Card authorisation is triggered from our own "Confirm Order" button (shared with
    // the bank account flow) rather than a second, separate button inside the widget.
    submitButton: {
      useBuiltInButton: false,
      onDisable: (isDisabled: boolean) => callbacks.onSubmittableChange(!isDisabled)
    },
    onCheckoutComplete: ({ payment }: { payment: PrimerCheckoutPayment }) => callbacks.onComplete(payment),
    onCheckoutFail: (
      error: unknown,
      data: { payment?: PrimerCheckoutPayment } | undefined,
      handler: PrimerCheckoutFailHandler | undefined
    ) => callbacks.onFail(error, data?.payment, handler),
    onCheckoutCancel: () => {
      if (callbacks.onCancel) {
        callbacks.onCancel();
      }
    }
  });

  return {
    submit: () => checkout.submit()
  };
}

function getNestedString (value: unknown, path: string[]): string | undefined {
  let current: unknown = value;
  for (const key of path) {
    if (typeof current !== 'object' || current === null) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Best-effort extraction of the card scheme/network from a completed payment payload.
 * Not currently used (bpo-website has no scheme-based surcharge logic), kept for parity
 * with the reference implementation and for future use.
 */
export function extractCardScheme (payment: PrimerCheckoutPayment): string {
  return getNestedString(payment, ['paymentMethod', 'paymentMethodData', 'binData', 'network']) ??
    getNestedString(payment, ['paymentMethodType']) ??
    '';
}
